import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFilterValues } from '../sbSharedRefineMenuHelpers';
import { createRefetchContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import { DEFAULT_LOCATION_LABELS } from '../sbSharedRefineMenuConstants';
import { getUserSessionCountryCode } from 'dibs-buyer-layout/exports/regionalInfoHelpers';
// components
import { SbSharedRefineMenuRadioSelectSearch } from '../SbSharedRefineMenuRadioSelect/SbSharedRefineMenuRadioSelectSearch';

import get from 'lodash.get';
import styles from './SbSharedRefineMenuFilterLocation.scss';

const getLocationsByZipCodeValues = viewer => {
    const locationsByZipCode = get(viewer, 'locations.edges') || [];
    return locationsByZipCode.map(location => {
        const { displayName, urlLabel, count, linkReference } = get(location, 'node') || {};
        return {
            displayName,
            urlLabel,
            count,
            linkReference,
        };
    });
};

export const SbSharedRefineMenuFilterLocationComponent = props => {
    const {
        values,
        itemSearch: { appliedFilters },
        filterName,
        viewer,
        relay,
        //redux
        userZipCode,
        uriRef,
        refetchInFlight,
    } = props;

    const [isLocationFetched, setIsLocationFetched] = useState(false);
    const [currentLocationsByZipCodeValues, updateCurrentLocationsByZipCodeValues] = useState(
        getLocationsByZipCodeValues(viewer)
    );

    useEffect(() => {
        setIsLocationFetched(false);
        relay.refetch(
            {
                userZipCode,
                userCountryCode: getUserSessionCountryCode(), //Todo this should be fetched from redux after we create country field. ENGAGE-1304
                uriRef,
            },
            null,
            () => setIsLocationFetched(true)
        );
    }, [uriRef, userZipCode, relay]);

    useEffect(() => {
        if (isLocationFetched && !refetchInFlight) {
            updateCurrentLocationsByZipCodeValues(getLocationsByZipCodeValues(viewer));
        }
    }, [viewer, isLocationFetched]); // eslint-disable-line react-hooks/exhaustive-deps

    const locationsByZipCodeValues = currentLocationsByZipCodeValues;

    const appliedFilterValues = values.filter(
        value => !locationsByZipCodeValues.includes(value.urlLabel)
    );

    const locationsByZipCodeLabels = locationsByZipCodeValues.map(location => {
        return location.urlLabel;
    });

    /**
     * We always want to show USA, EUROPE and then any active location filter in that order.
     * Make sure that we don't have duplicate locations from applied filters and facet search
     *
     * To accomplish this we will filter out then sort by their count
     *     USA count 400000
     *     Europe count 100000
     *     ...
     */

    const appliedFilterLabels = getFilterValues(appliedFilters, filterName).map(
        filter => filter.urlLabel
    );

    const displayLocationLabels = [
        ...DEFAULT_LOCATION_LABELS,
        ...locationsByZipCodeLabels,
        ...appliedFilterLabels,
    ];

    const displayValues = [...values, ...appliedFilterValues, ...locationsByZipCodeValues]
        .reduce((acc, curr) => {
            const { urlLabel } = curr;
            const isLocationAlreadyExists = acc.find(item => item.urlLabel === urlLabel);
            if (isLocationAlreadyExists) {
                return acc;
            } else if (displayLocationLabels.includes(curr.urlLabel)) {
                acc.push(curr);
            }
            return acc;
        }, [])
        .sort((a = {}, b = {}) => b.count - a.count);

    return (
        <React.Fragment>
            <div className={styles.title}>
                <FormattedMessage
                    id="sb.SbSharedRefineMenu.filter.location.title"
                    defaultMessage="Item Ships From"
                />
            </div>
            <SbSharedRefineMenuRadioSelectSearch {...props} values={displayValues} />
        </React.Fragment>
    );
};

SbSharedRefineMenuFilterLocationComponent.propTypes = {
    values: PropTypes.array.isRequired,
    filterName: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    userZipCode: PropTypes.string,
    uriRef: PropTypes.string,
    refetchInFlight: PropTypes.bool,
};

function mapStateToProps(state) {
    const uriRef = get(state, 'relayVariables.variables.uriRef');
    const userZipCode = get(state, 'filters.userZipCode');
    const refetchInFlight = get(state, 'filters.refetchInFlight');
    return { userZipCode, refetchInFlight, uriRef };
}

export const SbSharedRefineMenuFilterLocation = createRefetchContainer(
    connect(mapStateToProps)(SbSharedRefineMenuFilterLocationComponent),
    {
        viewer: graphql`
            fragment SbSharedRefineMenuFilterLocation_viewer on Viewer {
                ...SbSharedRefineMenuRadioSelectSearch_viewer
                locations: itemFacetSearch(
                    uriRef: $uriRef
                    facetName: "location"
                    first: 1
                    page: 1
                    getExactMatches: false
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) {
                    edges {
                        node {
                            count
                            displayName
                            urlLabel
                            linkReference
                        }
                    }
                }
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefineMenuFilterLocation_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuRadioSelectSearch_itemSearch
                appliedFilters {
                    name
                    values {
                        count
                        urlLabel
                        linkable
                        linkReference
                    }
                }
            }
        `,
    },
    graphql`
        query SbSharedRefineMenuFilterLocationRefetchQuery(
            $uriRef: String!
            $userZipCode: String
            $userCountryCode: String
        ) {
            viewer {
                ...SbSharedRefineMenuFilterLocation_viewer
            }
        }
    `
);
